/**
 * Retailer Alert Category Configuration Page
 * 
 * Manage alert categories: edit names (EN/FR), set priority (HIGH/MEDIUM/LOW),
 * enable/disable, reorder, add new, and delete.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Save,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const PRIORITY_CONFIG = {
  HIGH: { label: 'High', color: 'bg-red-500', text: 'text-red-400', badge: 'bg-red-500/20 text-red-400 border-red-500/30' },
  MEDIUM: { label: 'Medium', color: 'bg-amber-500', text: 'text-amber-400', badge: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  LOW: { label: 'Low', color: 'bg-green-500', text: 'text-green-400', badge: 'bg-green-500/20 text-green-400 border-green-500/30' },
};

export default function RetailerAlertConfiguration() {
  const { id: hotelId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('hotels_token') || localStorage.getItem('token');

  const [hotel, setHotel] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDefault, setIsDefault] = useState(true);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);

  // Form state
  const [newCategory, setNewCategory] = useState({
    name_en: '', name_fr: '', priority: 'MEDIUM',
  });

  const authHeaders = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  useEffect(() => { loadData(); }, [hotelId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [hotelRes, defRes] = await Promise.all([
        fetch(`${API_BASE}/api/hotels/${hotelId}`, { headers: authHeaders }),
        fetch(`${API_BASE}/api/hotels-standalone/organizations/${hotelId}/survey-definitions/retailer_alert`, { headers: authHeaders }),
      ]);
      if (hotelRes.ok) setHotel(await hotelRes.json());
      if (defRes.ok) {
        const defData = await defRes.json();
        setCategories(defData.categories || []);
        setIsDefault(defData.is_default || false);
      }
    } catch (e) {
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveCategories = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${hotelId}/survey-definitions/retailer_alert`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify({ categories }),
      });
      if (!res.ok) throw new Error('Failed to save');
      toast.success('Categories saved');
      setHasChanges(false);
      setIsDefault(false);
    } catch (e) {
      toast.error('Failed to save categories');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    setSaving(true);
    try {
      await fetch(`${API_BASE}/api/hotels-standalone/organizations/${hotelId}/survey-definitions/retailer_alert`, {
        method: 'DELETE', headers: authHeaders,
      });
      toast.success('Reset to defaults');
      setShowResetDialog(false);
      await loadData();
      setHasChanges(false);
    } catch (e) {
      toast.error('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  const moveCategory = (index, direction) => {
    const newCats = [...categories];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= newCats.length) return;
    [newCats[index], newCats[swapIdx]] = [newCats[swapIdx], newCats[index]];
    newCats.forEach((c, i) => c.order = i + 1);
    setCategories(newCats);
    setHasChanges(true);
  };

  const toggleCategory = (index) => {
    const newCats = [...categories];
    newCats[index].enabled = !newCats[index].enabled;
    setCategories(newCats);
    setHasChanges(true);
  };

  const updatePriority = (index, priority) => {
    const newCats = [...categories];
    newCats[index].priority = priority;
    setCategories(newCats);
    setHasChanges(true);
  };

  const addCategory = () => {
    if (!newCategory.name_en.trim()) { toast.error('English name is required'); return; }
    const id = newCategory.name_en.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/, '');
    const cat = {
      id: `custom_${id}_${Date.now()}`,
      name: { en: newCategory.name_en.trim(), fr: newCategory.name_fr.trim() || newCategory.name_en.trim() },
      icon: 'alert-triangle',
      order: categories.length + 1,
      enabled: true,
      priority: newCategory.priority,
    };
    setCategories([...categories, cat]);
    setNewCategory({ name_en: '', name_fr: '', priority: 'MEDIUM' });
    setShowAddModal(false);
    setHasChanges(true);
    toast.success('Category added');
  };

  const startEdit = (cat) => {
    setEditingCategory({
      ...cat,
      name_en: typeof cat.name === 'object' ? cat.name.en : cat.name,
      name_fr: typeof cat.name === 'object' ? (cat.name.fr || '') : '',
    });
    setShowEditModal(true);
  };

  const saveEdit = () => {
    if (!editingCategory.name_en.trim()) { toast.error('English name is required'); return; }
    const newCats = categories.map(c =>
      c.id === editingCategory.id
        ? { ...c, name: { en: editingCategory.name_en.trim(), fr: editingCategory.name_fr.trim() || editingCategory.name_en.trim() }, priority: editingCategory.priority }
        : c
    );
    setCategories(newCats);
    setShowEditModal(false);
    setEditingCategory(null);
    setHasChanges(true);
    toast.success('Category updated');
  };

  const confirmDelete = () => {
    if (!deletingCategory) return;
    setCategories(categories.filter(c => c.id !== deletingCategory.id));
    setShowDeleteDialog(false);
    setDeletingCategory(null);
    setHasChanges(true);
    toast.success('Category removed');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-fuchsia-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[hsl(222,47%,9%)] border-b border-[hsl(217,19%,20%)]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/hotels/${hotelId}`)} className="p-1.5 rounded-lg hover:bg-[hsl(217,19%,15%)]" data-testid="back-to-property">
              <ArrowLeft className="w-5 h-5 text-[hsl(215,20%,65%)]" />
            </button>
            <div>
              <h1 className="text-sm font-semibold text-white">Retailer Alert Categories</h1>
              <p className="text-xs text-[hsl(215,20%,50%)]">{hotel?.name || 'Property'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isDefault && (
              <Button variant="ghost" size="sm" onClick={() => setShowResetDialog(true)} className="text-[hsl(215,20%,55%)]" data-testid="reset-defaults-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Reset
              </Button>
            )}
            <Button
              size="sm" onClick={saveCategories}
              disabled={!hasChanges || saving}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50"
              data-testid="save-categories-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {hasChanges && (
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/25 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <p className="text-xs text-amber-400">You have unsaved changes</p>
          </div>
        )}

        {/* Category List */}
        <div className="space-y-2">
          {categories.map((cat, idx) => {
            const nameEn = typeof cat.name === 'object' ? cat.name.en : cat.name;
            const nameFr = typeof cat.name === 'object' ? cat.name.fr : '';
            const priority = cat.priority || 'MEDIUM';
            const pConfig = PRIORITY_CONFIG[priority];
            const enabled = cat.enabled !== false;

            return (
              <div
                key={cat.id}
                className={`p-4 rounded-lg border transition-all ${
                  enabled ? 'bg-[hsl(217,19%,14%)] border-[hsl(217,19%,24%)]' : 'bg-[hsl(217,19%,11%)] border-[hsl(217,19%,18%)] opacity-60'
                }`}
                data-testid={`category-row-${cat.id}`}
              >
                <div className="flex items-center gap-3">
                  {/* Reorder */}
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveCategory(idx, 'up')} disabled={idx === 0} className="p-0.5 rounded hover:bg-[hsl(217,19%,22%)] disabled:opacity-20">
                      <ChevronUp className="w-3.5 h-3.5 text-[hsl(215,20%,55%)]" />
                    </button>
                    <button onClick={() => moveCategory(idx, 'down')} disabled={idx === categories.length - 1} className="p-0.5 rounded hover:bg-[hsl(217,19%,22%)] disabled:opacity-20">
                      <ChevronDown className="w-3.5 h-3.5 text-[hsl(215,20%,55%)]" />
                    </button>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-white truncate">{nameEn}</p>
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border ${pConfig.badge}`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${pConfig.color} mr-1`} />
                        {pConfig.label}
                      </Badge>
                    </div>
                    {nameFr && nameFr !== nameEn && (
                      <p className="text-xs text-[hsl(215,20%,45%)] truncate mt-0.5">FR: {nameFr}</p>
                    )}
                  </div>

                  {/* Priority Select */}
                  <Select value={priority} onValueChange={(v) => updatePriority(idx, v)}>
                    <SelectTrigger className="w-[100px] h-8 text-xs bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]" data-testid={`priority-select-${cat.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HIGH">
                        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-red-500" />High</div>
                      </SelectItem>
                      <SelectItem value="MEDIUM">
                        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500" />Medium</div>
                      </SelectItem>
                      <SelectItem value="LOW">
                        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-green-500" />Low</div>
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Toggle */}
                  <Switch checked={enabled} onCheckedChange={() => toggleCategory(idx)} data-testid={`toggle-${cat.id}`} />

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(cat)} className="p-1.5 rounded hover:bg-[hsl(217,19%,22%)] text-[hsl(215,20%,55%)] hover:text-white" data-testid={`edit-${cat.id}`}>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    </button>
                    <button onClick={() => { setDeletingCategory(cat); setShowDeleteDialog(true); }} className="p-1.5 rounded hover:bg-red-500/20 text-[hsl(215,20%,45%)] hover:text-red-400" data-testid={`delete-${cat.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add Button */}
        <Button
          variant="outline"
          onClick={() => { setNewCategory({ name_en: '', name_fr: '', priority: 'MEDIUM' }); setShowAddModal(true); }}
          className="w-full border-dashed border-[hsl(217,19%,27%)] text-[hsl(215,20%,60%)] hover:text-white hover:border-fuchsia-500/40"
          data-testid="add-category-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Category
        </Button>

        <p className="text-xs text-center text-[hsl(215,20%,40%)]">
          Changes will apply to the customer-facing alert form after saving.
        </p>
      </div>

      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,22%)]">
          <DialogHeader>
            <DialogTitle className="text-white">Add Alert Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-[hsl(215,20%,65%)]">Name (English) *</Label>
              <Input value={newCategory.name_en} onChange={(e) => setNewCategory({ ...newCategory, name_en: e.target.value })}
                placeholder="e.g., Broken Equipment"
                className="mt-1 bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
                data-testid="add-category-name-en"
              />
            </div>
            <div>
              <Label className="text-xs text-[hsl(215,20%,65%)]">Name (French)</Label>
              <Input value={newCategory.name_fr} onChange={(e) => setNewCategory({ ...newCategory, name_fr: e.target.value })}
                placeholder="e.g., Équipement cassé"
                className="mt-1 bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
                data-testid="add-category-name-fr"
              />
            </div>
            <div>
              <Label className="text-xs text-[hsl(215,20%,65%)]">Priority Level</Label>
              <Select value={newCategory.priority} onValueChange={(v) => setNewCategory({ ...newCategory, priority: v })}>
                <SelectTrigger className="mt-1 bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]" data-testid="add-category-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HIGH"><div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-red-500" />High</div></SelectItem>
                  <SelectItem value="MEDIUM"><div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500" />Medium</div></SelectItem>
                  <SelectItem value="LOW"><div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-green-500" />Low</div></SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={addCategory} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="confirm-add-category">Add Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,22%)]">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Category</DialogTitle>
          </DialogHeader>
          {editingCategory && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs text-[hsl(215,20%,65%)]">Name (English) *</Label>
                <Input value={editingCategory.name_en} onChange={(e) => setEditingCategory({ ...editingCategory, name_en: e.target.value })}
                  className="mt-1 bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
                  data-testid="edit-category-name-en"
                />
              </div>
              <div>
                <Label className="text-xs text-[hsl(215,20%,65%)]">Name (French)</Label>
                <Input value={editingCategory.name_fr} onChange={(e) => setEditingCategory({ ...editingCategory, name_fr: e.target.value })}
                  className="mt-1 bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
                  data-testid="edit-category-name-fr"
                />
              </div>
              <div>
                <Label className="text-xs text-[hsl(215,20%,65%)]">Priority Level</Label>
                <Select value={editingCategory.priority || 'MEDIUM'} onValueChange={(v) => setEditingCategory({ ...editingCategory, priority: v })}>
                  <SelectTrigger className="mt-1 bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]" data-testid="edit-category-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HIGH"><div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-red-500" />High</div></SelectItem>
                    <SelectItem value="MEDIUM"><div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500" />Medium</div></SelectItem>
                    <SelectItem value="LOW"><div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-green-500" />Low</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="confirm-edit-category">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,22%)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Category</AlertDialogTitle>
            <AlertDialogDescription className="text-[hsl(215,20%,55%)]">
              Remove "{deletingCategory && (typeof deletingCategory.name === 'object' ? deletingCategory.name.en : deletingCategory.name)}"? This won't affect existing alerts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-category">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Confirmation */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent className="bg-[hsl(222,47%,11%)] border-[hsl(217,19%,22%)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Reset to Defaults</AlertDialogTitle>
            <AlertDialogDescription className="text-[hsl(215,20%,55%)]">
              This will revert all categories to the system defaults. Your custom categories will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={resetToDefaults} className="bg-red-600 hover:bg-red-700" data-testid="confirm-reset">Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
